import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { environment } from '@env';
import { ImpersonationSessionService } from '@features/authentication/services/impersonation-session.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import type { UserType } from '@kerberos-compliance/compliance-shared-ts/features/user/enums/user.enums';
import { UserTypeMap } from '@kerberos-compliance/compliance-shared-ts/features/user/enums/user.enums';
import { ButtonComponent, ButtonSizeMap, ButtonVariantMap } from '@kerberos-compliance/compliance-shared-ui';

const UserTypeTranslationKeys: Record<UserType, string> = {
  [UserTypeMap.Default]: 'users.userTypes.Default',
  [UserTypeMap.Employee]: 'users.userTypes.Employee',
  [UserTypeMap.ServiceUser]: 'users.userTypes.ServiceUser',
  [UserTypeMap.ExternalUser]: 'users.userTypes.ExternalUser',
};

@Component({
  selector: 'aad-impersonation-banner',
  templateUrl: './impersonation-banner.component.html',
  styleUrl: './impersonation-banner.component.less',
  imports: [TranslocoDirective, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImpersonationBannerComponent {
  private readonly sessionService = inject(ImpersonationSessionService);

  private readonly translocoService = inject(TranslocoService);

  protected readonly session = this.sessionService.session;

  protected readonly ButtonVariantMap = ButtonVariantMap;

  protected readonly ButtonSizeMap = ButtonSizeMap;

  protected readonly userTypeLabel = computed(() => {
    const session = this.session();
    return session ? this.translocoService.translate(this.getUserTypeLabel(session.userType)) : '';
  });

  /**
   * Retrieves the label translation key for a given user type.
   *
   * @param {UserType} userType - The type of user for which the label translation key is requested.
   * @returns {string} The translation key for the given user type, or a default value if the user type is not available.
   */
  protected getUserTypeLabel(userType: UserType): string {
    return UserTypeTranslationKeys[userType] ?? 'users.detailPage.summary.notAvailable';
  }

  /**
   * Handles the action to switch back to a predefined target URL.
   * It ensures the URL specified in the environment configuration is valid before performing the operation.
   * The method uses the session service to execute the switch back operation.
   *
   * @returns {void} This method does not return any value.
   */
  public onSwitchBack(): void {
    const targetUrl = environment.amlDeskPlatformUrl;
    if (!targetUrl) {
      return;
    }

    this.sessionService.switchBack(targetUrl);
  }
}
